import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { ButtonBase, Menu, MenuItem, Stack, Typography } from '@mui/material';
import Logger from 'logger';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import PaiTooltip from './pai-tooltip';
import { OptionType } from './pai-auto-complete';
import _ from 'lodash';

interface MenuProps {
  open: boolean;
  anchorEl: null | HTMLElement;
  options: OptionType[];
  setCopied: React.Dispatch<React.SetStateAction<boolean>>;

  handleClose(): void;
}

const PaiPlaceholderMenu: React.FC<MenuProps> = ({ open, anchorEl, options, setCopied, handleClose }) => {
  const { classes } = useStyles();

  const copyTextToClipboard = (text: string) => {
    return async () => {
      try {
        await navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 3000);
        handleClose();
      } catch (e) {
        // fallback for outlook in IFrame
        // for more information see here: https://web.dev/async-clipboard/#permissions-policy-integration
        Logger.error(e);
      }
    };
  };

  return (
    <Menu
      anchorEl={anchorEl}
      open={open}
      onClose={handleClose}
      onClick={handleClose}
      PaperProps={{
        elevation: 0,
        sx: {
          borderRadius: '4px',
          padding: 0,
          overflow: 'visible',
          filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
          mt: 1.5,
          '& .MuiAvatar-root': {
            width: 32,
            height: 32,
            ml: -0.5,
            mr: 1,
          },
          '&:before': {
            content: '""',
            display: 'block',
            position: 'absolute',
            top: 0,
            right: 14,
            width: 10,
            height: 10,
            bgcolor: 'background.paper',
            transform: 'translateY(-50%) rotate(45deg)',
            zIndex: 0,
          },
        },
      }}
      transformOrigin={{ horizontal: 'center', vertical: 'top' }}
      anchorOrigin={{ horizontal: 'center', vertical: 'bottom' }}
    >
      {options.map((option, index) => (
        <MenuItem key={index} className={classes.menuItem} onClick={copyTextToClipboard(option.value)}>
          <Stack gap={2} direction={'row'} justifyContent={'space-between'} alignItems={'center'} width={'100%'}>
            <span>{option.label}</span>
            <ContentCopyIcon className={classes.icon} />
          </Stack>
        </MenuItem>
      ))}
    </Menu>
  );
};

interface Props {
  options: OptionType[];
}

const PaiPlaceholders: React.FC<Props> = ({ options }) => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(anchorEl);
  const [copied, setCopied] = useState(false);

  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  return (
    <>
      <PaiTooltip title={t('copied')} open={copied} placement={'right'}>
        <ButtonBase
          className={classes.centered}
          onClick={handleOpenMenu}
          disableRipple={true}
          style={{ width: 'fit-content', visibility: !_.isEmpty(options) ? 'visible' : 'hidden' }}
        >
          <Stack direction={'row'} gap={1}>
            <Typography className={classes.text}>{t('insertPlaceholders')}</Typography>
            <ExpandMoreIcon className={classes.expandIcon} />
          </Stack>
        </ButtonBase>
      </PaiTooltip>

      <PaiPlaceholderMenu open={menuOpen} anchorEl={anchorEl} options={options} setCopied={setCopied} handleClose={handleCloseMenu} />
    </>
  );
};

const useStyles = makeStyles()((theme) => ({
  text: {
    fontSize: theme.typography.pxToRem(13),
    lineHeight: 1,
  },
  expandIcon: {
    fontSize: theme.typography.pxToRem(16),
    color: theme.palette.greys.medium,
  },
  centered: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: '6px',
    padding: '4px 12px',
    '&:hover': {
      background: theme.palette.action.hover,
    },
  },
  menuItem: {
    fontSize: theme.typography.pxToRem(13),
    padding: '4px 8px',
  },
  icon: {
    fontSize: theme.typography.pxToRem(16),
    color: theme.palette.primary.main,
    marginLeft: '8px',
  },
}));

export default PaiPlaceholders;
